import { supabase } from "../supabase";
import { formatSupabaseError } from "./estimates";
import {
  computeLabourTotals,
  computeTotalLabourCost,
  groupLabourByEmployee,
} from "./labourCost";
import {
  enrichTimelogWithQuoteLink,
  persistTimelogQuoteLineLink,
} from "./projectQuoteLineMatching";
import { scopeOperationalTimeLogsForActor } from "./scopedQueries";
import { applyActiveTimelogFilter } from "./timeLogVisibility";
import {
  getLabourSelectColumns,
  getTimeLogsRelationName,
} from "./operationalProjections";

const DEFAULT_LABOUR_SELECT =
  "id, employee, supabase_user_id, date_iso, total_hours, hourly_rate, net_work_seconds, quote_line_snapshot_id";

function wrapSupabaseError(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  throw wrapped;
}

const HUB_LABOUR_SELECT =
  "project_id, total_hours, net_work_seconds, hourly_rate";

const HUB_LABOUR_SELECT_OPERATIONAL =
  "project_id, total_hours, net_work_seconds";

/**
 * Apply organization scope only. Row visibility for project aggregation is
 * enforced by RLS (manager+ project ledger) or operational views (employees).
 */
async function scopeLabourQuery(query, actor) {
  if (!actor) {
    return query;
  }

  return scopeOperationalTimeLogsForActor(query, supabase, actor);
}

export async function loadLabourForProject(
  projectId,
  { select = null, actor = null, orgPolicies = null } = {},
) {
  if (!projectId) return [];

  const relation = getTimeLogsRelationName(actor, orgPolicies);
  const resolvedSelect =
    select ?? getLabourSelectColumns(actor, orgPolicies);

  let query = supabase
    .from(relation)
    .select(resolvedSelect)
    .eq("project_id", projectId)
    .order("date_iso", { ascending: false });

  query = applyActiveTimelogFilter(query);
  query = await scopeLabourQuery(query, actor);

  const { data, error } = await query;

  if (error) wrapSupabaseError(error);
  return data || [];
}

export async function loadLabourForProjects(
  projectIds = [],
  { select = null, actor = null, orgPolicies = null } = {},
) {
  if (!projectIds.length) return [];

  const relation = getTimeLogsRelationName(actor, orgPolicies);
  const resolvedSelect =
    select ??
    (relation === "time_logs"
      ? HUB_LABOUR_SELECT
      : HUB_LABOUR_SELECT_OPERATIONAL);

  let query = supabase
    .from(relation)
    .select(resolvedSelect)
    .in("project_id", projectIds);

  query = applyActiveTimelogFilter(query);
  query = await scopeLabourQuery(query, actor);

  const { data, error } = await query;

  if (error) wrapSupabaseError(error);
  return data || [];
}

export function sumLabourCost(logs = []) {
  return computeTotalLabourCost(logs);
}

export function buildLabourTotals(logs = []) {
  return computeLabourTotals(logs);
}

export function groupLabourLogsByEmployee(logs = []) {
  return groupLabourByEmployee(logs);
}

export async function resolveTimeLogId(sessionId, actor = null) {
  if (!sessionId) return null;

  let query = applyActiveTimelogFilter(
    supabase.from("time_logs").select("id").eq("session_id", sessionId),
  );
  query = await scopeLabourQuery(query, actor);

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error("TIME LOG LOOKUP ERROR:", error);
    return null;
  }

  return data?.id ?? null;
}

export async function loadTimelogBySessionId(sessionId, actor = null) {
  if (!sessionId) return null;

  let query = applyActiveTimelogFilter(
    supabase
      .from("time_logs")
      .select(
        "id, project_id, supabase_user_id, date_iso, work_completed, work_category, trade_name, quote_line_snapshot_id",
      )
      .eq("session_id", sessionId),
  );
  query = await scopeLabourQuery(query, actor);

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error("TIME LOG LOOKUP ERROR:", error);
    return null;
  }

  return data;
}

export async function applyQuoteLineLinkToTimelogRow(timelog = {}) {
  try {
    return await enrichTimelogWithQuoteLink(timelog);
  } catch (error) {
    console.warn(
      "[projectLabourLedger] quote line link skipped; labour stop continues",
      error,
    );
    return timelog;
  }
}

export async function ensureTimelogQuoteLineLink(
  timelogId,
  projectId,
  timelogFields = {},
) {
  return persistTimelogQuoteLineLink(timelogId, projectId, timelogFields);
}
